// Error Handler - Zentrale Fehlerbehandlung
import { kvSet, kvRemove } from '../infrastructure/storage/kvAccess';
import { persistLastCrash } from '../infrastructure/telemetry/crashDebug';

class ErrorHandler {
  constructor() {
    this.errorLog = [];
    this.maxLogSize = 100;
    this.setupGlobalErrorHandling();
  }

  setupGlobalErrorHandling() {
    // Unhandled Promise Rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handle(event.reason, 'Unhandled Promise Rejection', {
        promise: event.promise,
        type: 'unhandledRejection'
      });
    });

    // Global Errors
    window.addEventListener('error', (event) => {
      this.handle(event.error, 'Global Error', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        type: 'globalError'
      });
    });
  }

  handle(error, context, metadata = {}) {
    const errorEntry = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      context,
      error: {
        message: error?.message || 'Unknown error',
        stack: error?.stack || '',
        name: error?.name || 'Error'
      },
      metadata,
      severity: this.getSeverity(error),
      userMessage: this.getUserMessage(error, context)
    };

    // Error loggen
    this.logError(errorEntry);

    // Always-on crash breadcrumb (visible in ErrorBoundary / Bootstrap diagnostic)
    try {
      persistLastCrash(error, { context: String(context || 'errorHandler') });
    } catch {
      /* ignore */
    }

    // Console logging
    console.error(`❌ [${context}]`, error);

    // Benutzer-Benachrichtigung bei kritischen Fehlern
    if (errorEntry.severity === 'critical') {
      this.showUserNotification(errorEntry.userMessage, 'error');
    }

    return errorEntry;
  }

  logError(errorEntry) {
    this.errorLog.push(errorEntry);
    
    // Log-Größe begrenzen
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog.shift();
    }

    // In localStorage speichern
    try {
      kvSet('error_log', this.errorLog);
    } catch (error) {
      console.error('Failed to save error log:', error);
    }
  }

  getSeverity(error) {
    if (error?.message?.includes('Network') || error?.message?.includes('fetch')) {
      return 'warning';
    }
    if (error?.message?.includes('Validation') || error?.message?.includes('Invalid')) {
      return 'medium';
    }
    if (error?.message?.includes('Session') || error?.message?.includes('Authentication')) {
      return 'high';
    }
    if (error?.message?.includes('Database') || error?.message?.includes('Storage')) {
      return 'critical';
    }
    return 'low';
  }

  getUserMessage(error, context) {
    const message = error?.message || 'Ein unbekannter Fehler ist aufgetreten';
    
    // Kontext-spezifische Nachrichten
    if (context.includes('Session')) {
      return 'Sitzungsfehler: Bitte melden Sie sich erneut an.';
    }
    if (context.includes('Deviation')) {
      return 'Berechnungsfehler: Die Abweichungsberechnung konnte nicht durchgeführt werden.';
    }
    if (context.includes('Validation')) {
      return 'Eingabefehler: Bitte überprüfen Sie Ihre Eingaben.';
    }
    if (context.includes('Network')) {
      return 'Verbindungsfehler: Bitte überprüfen Sie Ihre Internetverbindung.';
    }
    if (context.includes('Storage')) {
      return 'Speicherfehler: Daten konnten nicht gespeichert werden.';
    }
    
    return `Fehler in ${context}: ${message}`;
  }

  showUserNotification(message, type = 'error') {
    try {
      // Check if document and body exist
      if (!document || !document.body) {
        console.warn('Document or body not available for notification');
        return;
      }

      // Einfache Benachrichtigung (kann später durch Toast-System ersetzt werden)
      const notification = document.createElement('div');
      notification.className = `ads-runtime-toast ads-runtime-toast--${type === 'error' ? 'error' : 'success'}`;
      notification.setAttribute('role', 'status');
      notification.textContent = message;
      
      document.body.appendChild(notification);
      
      // Nach 5 Sekunden entfernen
      setTimeout(() => {
        try {
          if (document.body && document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        } catch (error) {
          console.error('Error removing notification:', error);
        }
      }, 5000);
    } catch (error) {
      console.error('Error showing user notification:', error);
    }
  }

  getErrorLog() {
    return this.errorLog;
  }

  clearErrorLog() {
    this.errorLog = [];
    kvRemove('error_log');
  }

  getErrorStats() {
    const stats = {
      total: this.errorLog.length,
      bySeverity: {},
      byContext: {},
      recent: this.errorLog.slice(-10)
    };

    this.errorLog.forEach(error => {
      // Nach Schweregrad
      stats.bySeverity[error.severity] = (stats.bySeverity[error.severity] || 0) + 1;
      
      // Nach Kontext
      stats.byContext[error.context] = (stats.byContext[error.context] || 0) + 1;
    });

    return stats;
  }

  generateId() {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Singleton-Instanz
const errorHandler = new ErrorHandler();

export default errorHandler;
